//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import IItemMetadata, { IRightsRequestState } from '@models/IItemMetadata';
import PartialCuid from '@models/PartialCuid';
import ReelId from '@models/ReelId';
import { gatewayFetch, serializedSessionFetch } from '@utils/sessionFetch';
import { getRightsRequestStatuses } from './gatewayMediaApi';
import { deserializeRightsPermissions } from '@apis/rights-management/rightsManagementApiUtils';
import { deserializeItemMetadataTags } from '@apis/universal-image-actions/universalImageActionsApiUtils';

function bulkApiFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  return gatewayFetch('web_gateway', `/api/v1/media${endpoint}`, init);
}

export async function addToGalleries(
  cuids: PartialCuid[],
  reelIds: ReelId[],
  skipRightsOnItems?: boolean,
): Promise<{ cuids: PartialCuid[] }> {
  const res = await bulkApiFetch(`/galleries`, {
    method: 'POST',
    body: JSON.stringify({
      cuids,
      reelIds,
      skipRightsOnItems,
    }),
  });
  return await res.json();
}

/**
 * Adds / removes the given labels to the media items associated with the given cuids.
 *
 * /api/v1/media/labels
 */
export async function updateLabels(
  cuids: PartialCuid[],
  labels: {
    labelsToAdd?: string[];
    labelsToRemove?: string[];
  },
): Promise<unknown> {
  const params = {
    cuids,
    addLabels: labels.labelsToAdd ?? [],
    removeLabels: labels.labelsToRemove ?? [],
  };

  const res = await bulkApiFetch('/labels', {
    method: 'PUT',
    body: JSON.stringify(params),
  });
  return res.json();
}

export async function getItemsMetadata(
  cuids: PartialCuid[],
): Promise<IItemMetadata[]> {
  const metadataPromises: Promise<IItemMetadata>[] = cuids.map(
    (cuid: PartialCuid) =>
      serializedSessionFetch(
        `/api/universal-image-actions/items/${cuid}/metadata`,
      ).then((res) =>
        res.json<IItemMetadata>((res) => {
          return {
            ...res,
            productTags: deserializeItemMetadataTags(res.productTags),
            rightsPermissions: deserializeRightsPermissions(
              res.rightsPermissions,
            ),
          };
        }),
      ),
  );

  const rightsStatusPromise = getRightsRequestStatuses(cuids);

  return await Promise.all([
    Promise.all(metadataPromises),
    rightsStatusPromise,
  ]).then(([metadata, rightsStatusMap]) =>
    cuids.map((cuid: PartialCuid, index: number) => ({
      ...metadata[index],
      rightsRequestStatus: Object.values<IRightsRequestState>(
        rightsStatusMap.data,
      )[Object.keys(rightsStatusMap.data).indexOf(cuid)],
    })),
  );
}
